import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {Calculator, Award, Info} from 'lucide-react';
import { Link } from "react-router-dom";

function USGACalculator() {
  const [handicapIndex, setHandicapIndex] = useState("");
  const [courseSlope, setCourseSlope] = useState("113");
  const [courseRating, setCourseRating] = useState("");
  const [coursePar, setCoursePar] = useState("72");
  const [result, setResult] = useState<number | null>(null);

  const calculateCourseHandicap = () => {
    const index = parseFloat(handicapIndex);
    const slope = parseFloat(courseSlope);
    const rating = parseFloat(courseRating);
    const par = parseFloat(coursePar);

    if (index && slope && rating && par && slope > 0) {
      const courseHandicap = index * (slope / 113) + (rating - par);
      setResult(Math.round(courseHandicap));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              USGA Course Handicap Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Convert your handicap index to a course-specific handicap. Essential for tournament play and fair competition on any golf course.
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#006747] rounded-lg flex items-center justify-center">
                <Calculator className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Calculate Course Handicap
              </h2>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Handicap Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={handicapIndex}
                  onChange={(e) => {
                    setHandicapIndex(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 12.4"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Course Slope Rating
                </label>
                <input
                  type="number"
                  value={courseSlope}
                  onChange={(e) => {
                    setCourseSlope(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 113"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Course Rating
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={courseRating}
                  onChange={(e) => {
                    setCourseRating(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 71.5"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Course Par
                </label>
                <input
                  type="number"
                  value={coursePar}
                  onChange={(e) => {
                    setCoursePar(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 72"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <button
                onClick={calculateCourseHandicap}
                className="w-full bg-[#006747] text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#005239] transition-colors shadow-lg"
              >
                Calculate Course Handicap
              </button>

              {result !== null ? (
                <div className="bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <Award className="text-[#006747]" size={28} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Your Course Handicap:
                    </h3>
                  </div>
                  <p className="text-4xl font-bold text-[#006747] mb-3">
                    {result}
                  </p>
                  <p className="text-sm text-gray-600">
                    This is your playing handicap for this specific course. Use this number when posting your score.
                  </p>
                  <Link
                    to="/formulas"
                    className="inline-flex items-center gap-2 text-[#006747] font-medium mt-3 hover:underline"
                  >
                    <Info size={16} />
                    How was this calculated?
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Understanding USGA Course Handicaps
          </h2>

          <div className="space-y-6 text-gray-700">
            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#006747]">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What is a Course Handicap?
              </h3>
              <p className="mb-4">
                Your course handicap adjusts your handicap index to the specific difficulty of the course you're playing. It accounts for both the slope rating and the difference between course rating and par.
              </p>
              <div className="bg-white p-4 rounded border border-gray-200">
                <code className="text-gray-800 font-mono text-sm">
                  Course Handicap = Handicap Index × (Slope / 113) + (Course Rating - Par)
                </code>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Why Does It Matter?
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Ensures fair competition across different courses</li>
                  <li>• Required for tournament play</li>
                  <li>• Adjusts for course difficulty variations</li>
                  <li>• Official USGA standard calculation</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Where to Find Course Info
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Course scorecards</li>
                  <li>• Pro shop or clubhouse</li>
                  <li>• GHIN website or app</li>
                  <li>• Course rating books</li>
                </ul>
              </div>
            </div>

            <div className="bg-[#F0FDF4] rounded-lg p-6 border border-[#22C55E]">
              <h4 className="font-semibold text-gray-900 mb-2">
                📌 Important Note
              </h4>
              <p className="text-gray-700">
                <strong>Handicap Index vs. Course Handicap:</strong> Your handicap index is portable across all courses. Your course handicap is specific to the tees you're playing from. Always calculate your course handicap before starting your round.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              className="flex-1 bg-[#006747] text-white text-center py-3 px-6 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
            >
              Calculate Handicap Index
            </Link>
            <Link
              to="/formulas"
              className="flex-1 bg-white text-[#006747] text-center py-3 px-6 rounded-lg font-semibold border-2 border-[#006747] hover:bg-gray-50 transition-colors"
            >
              View All Formulas
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default USGACalculator;
